"use client";
import React, { useState } from "react";
import { motion } from "framer-motion";
import ProjectModal from "./ProjectModal";
import Image from "next/image";

/* cardEntry variant — imported or defined locally */
const cardEntry = {
  hidden: { y: 50, opacity: 0, scale: 0.97 },
  show: {
    y: 0,
    opacity: 1,
    scale: 1,
    transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] },
  },
  exit: {
    y: -30,
    opacity: 0,
    scale: 0.97,
    transition: { duration: 0.35, ease: "easeIn" },
  },
};

function FeaturedCard({ p }) {
  const [hovered, setHovered] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const year = p?.completion_date
    ? new Date(p.completion_date).getFullYear()
    : p?.createdAt
      ? new Date(p.createdAt).getFullYear()
      : "—";

  return (
    <>
      <motion.article
        variants={cardEntry}
        onHoverStart={() => setHovered(true)}
        onHoverEnd={() => setHovered(false)}
        whileHover={{ y: -5 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        onClick={() => setModalOpen(true)}
        className="relative col-span-1 md:col-span-2 lg:col-span-2 overflow-hidden rounded-2xl group cursor-pointer
          border border-white/[0.07] bg-white/[0.025]
          hover:border-[#00f0ff]/25 transition-colors duration-400"
      >
        {/* Inner glow */}
        <div
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
          style={{
            background:
              "linear-gradient(135deg, rgba(0,71,255,0.08) 0%, transparent 50%)",
          }}
        />

        {/* Corner brackets */}
        <svg
          className="absolute top-4 left-4 z-20 opacity-25 group-hover:opacity-60 transition-opacity duration-300"
          width="18"
          height="18"
          fill="none"
        >
          <path d="M0 18 L0 0 L18 0" stroke="#00f0ff" strokeWidth="1.5" />
        </svg>
        <svg
          className="absolute bottom-4 right-4 z-20 opacity-25 group-hover:opacity-60 transition-opacity duration-300"
          width="18"
          height="18"
          fill="none"
        >
          <path d="M18 0 L18 18 L0 18" stroke="#0047ff" strokeWidth="1.5" />
        </svg>

        {/* Featured badge */}
        <div className="absolute top-4 right-4 z-20">
          <span
            className="flex items-center gap-1.5 px-3 py-1 rounded-full text-[9px] tracking-[0.2em] uppercase border border-[#00f0ff]/25 bg-[#05050b]/70 backdrop-blur-sm text-[#00f0ff]/70"
            style={{ fontFamily: "'DM Mono', monospace" }}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#00f0ff]" />
            Featured
          </span>
        </div>

        <div className="flex flex-col md:flex-row h-full">
          {/* Image */}
          <div className="relative md:w-3/5 aspect-[16/10] md:aspect-auto md:min-h-[320px] overflow-hidden bg-white/[0.03] flex-shrink-0">
            {p?.thumbnail ? (
              <>
                <motion.div
                  className="absolute inset-0"
                  animate={hovered ? { scale: 1.05 } : { scale: 1 }}
                  transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                >
                  <Image
                    src={p.thumbnail}
                    alt={p?.projects_name}
                    fill
                    className="w-full h-full object-cover"
                  />
                </motion.div>
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(to right, transparent 50%, rgba(5,5,11,0.9) 100%)",
                  }}
                />
                <div
                  className="absolute inset-0 pointer-events-none opacity-25"
                  style={{
                    background:
                      "repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(0,0,0,0.07) 3px, rgba(0,0,0,0.07) 4px)",
                  }}
                />
              </>
            ) : (
              <div
                className="w-full h-full"
                style={{
                  background: "linear-gradient(135deg, #0047ff10, #00f0ff05)",
                }}
              />
            )}

            {/* "Click to view" hover pill — center of image */}
            <motion.div
              className="absolute inset-0 z-10 flex items-center justify-center"
              animate={{ opacity: hovered ? 1 : 0 }}
              transition={{ duration: 0.25 }}
            >
              <motion.div
                initial={{ scale: 0.85, opacity: 0 }}
                animate={
                  hovered
                    ? { scale: 1, opacity: 1 }
                    : { scale: 0.85, opacity: 0 }
                }
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                className="flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/20 bg-[#05050b]/80 backdrop-blur-md"
              >
                <span
                  className="text-[10px] tracking-[0.18em] uppercase text-white/70"
                  style={{ fontFamily: "'DM Mono', monospace" }}
                >
                  View Details
                </span>
                <span className="text-[#00f0ff] text-sm">↗</span>
              </motion.div>
            </motion.div>
          </div>

          {/* Content */}
          <div className="relative z-10 flex flex-col justify-center p-7 md:p-10 flex-1">
            {p?.categoryId?.category_name && (
              <span
                className="text-[9px] tracking-[0.28em] uppercase text-[#00f0ff]/50 mb-3"
                style={{ fontFamily: "'DM Mono', monospace" }}
              >
                {p.categoryId.category_name}
              </span>
            )}
            <h3
              className="text-2xl md:text-3xl font-black text-white leading-tight mb-3"
              style={{ fontFamily: "'Syne', sans-serif" }}
            >
              {p?.projects_name || "Untitled"}
            </h3>
            <motion.div
              className="h-px mb-4"
              animate={hovered ? { width: "80%" } : { width: 40 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              style={{
                background:
                  "linear-gradient(90deg, #0047ff, #00f0ff60, transparent)",
                minWidth: 40,
              }}
            />
            {p?.short_description && (
              <p
                className="text-sm text-white/40 leading-relaxed mb-5 line-clamp-3"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                {p.short_description?.replace(/<[^>]*>/g, "")}
              </p>
            )}
            {p?.skills?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-6">
                {p.skills.slice(0, 4).map((t, i) => (
                  <span
                    key={i}
                    className="px-2.5 py-0.5 rounded-full text-[9px] tracking-[0.14em] uppercase border border-white/[0.07] text-white/30"
                    style={{ fontFamily: "'DM Mono', monospace" }}
                  >
                    {t}
                  </span>
                ))}
                {p.skills.length > 4 && (
                  <span
                    className="text-[9px] text-white/18 py-0.5"
                    style={{ fontFamily: "'DM Mono', monospace" }}
                  >
                    +{p.skills.length - 4}
                  </span>
                )}
              </div>
            )}
            <div className="flex items-center justify-between">
              <span
                className="text-[9px] tracking-[0.22em] text-white/18 uppercase"
                style={{ fontFamily: "'DM Mono', monospace" }}
              >
                {year}
              </span>
              <div
                className="flex items-center gap-2 text-[10px] tracking-[0.18em] uppercase text-[#00f0ff]/50 group-hover:text-[#00f0ff] transition-colors duration-300"
                style={{ fontFamily: "'DM Mono', monospace" }}
              >
                View Details ↗
              </div>
            </div>
          </div>
        </div>
      </motion.article>

      {modalOpen && (
        <ProjectModal project={p} onClose={() => setModalOpen(false)} />
      )}
    </>
  );
}

export default FeaturedCard;
